import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  Link,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

interface OperatingArea {
  name: string;
  short_name?: string;
  location?: { lat?: string; lng?: string; zoom?: string };
}

export default function Info() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [areas, setAreas] = useState<OperatingArea[]>([]);
  // Latest ATO day with data + per-area mission counts, so tiles say what is behind them.
  const [latest, setLatest] = useState<{ date: string; byArea: Record<string, number> } | null>(null);
  useEffect(() => {
    fetch('/api/main/dates')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.latest) setLatest({ date: d.latest, byArea: d.byArea || {} }); })
      .catch(() => {});
  }, []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAreas = async () => {
      try {
        const response = await fetch('/api/operatingAreas');
        if (!response.ok) throw new Error('Failed to fetch operating areas');
        const data = await response.json();
        setAreas(Array.isArray(data) ? data : []);
      } catch (err) {
        setError('Failed to load operating areas');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAreas();
  }, []);

  const handleAreaClick = (area: OperatingArea) => {
    const identifier = area.short_name || area.name;
    const dateQ = latest ? `&atoDate=${latest.date}` : '';
    navigate(`/main?operatingArea=${encodeURIComponent(identifier)}&group=PLATFORM${dateQ}`);
  };

  const handleAllAreas = () => {
    navigate(latest ? `/main?atoDate=${latest.date}` : '/main');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 1400, mx: 'auto' }}>
      {/* Header Block */}
      <Paper
        sx={{
          background: 'linear-gradient(135deg, #212229 0%, #1a1c26 100%)',
          border: '1px solid #333',
          p: 4,
          textAlign: 'center',
        }}
      >
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 'bold',
            mb: 1,
            color: '#ffffff',
          }}
        >
          AUSS<span style={{ color: '#00b8ff' }}>IDEX</span>
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: '#cacce5',
            mb: 2,
            fontWeight: 300,
          }}
        >
          IDEX Mission Planning System Replica
        </Typography>
        <Chip
          label="v1.0"
          component={Link}
          href="https://github.com/bradrushworth/aussidex"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            backgroundColor: 'rgba(0,184,255,0.15)',
            color: '#00b8ff',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'rgba(0,184,255,0.25)',
            },
          }}
        />
      </Paper>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Center: Operating Areas */}
        <Grid item xs={12} md={8}>
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                mb: 2,
                color: '#ffffff',
              }}
            >
              OPERATING AREAS
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress sx={{ color: '#f72bef' }} />
              </Box>
            ) : error ? (
              <Alert severity="warning">{error}</Alert>
            ) : areas.length === 0 ? (
              <Alert severity="info">
                Run the sync script to load operating areas.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {/* ALL AREAS tile */}
                <Grid item xs={12} sm={6} md={4}>
                  <Paper
                    onClick={handleAllAreas}
                    sx={{
                      p: 3,
                      cursor: 'pointer',
                      backgroundColor: '#1a1c26',
                      border: '1px solid #333',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: '#f72bef',
                        boxShadow: '0 0 16px rgba(247, 43, 239, 0.2)',
                      },
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 'bold',
                        color: '#00b8ff',
                        mb: 1,
                      }}
                    >
                      ALL AREAS
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#cacce5',
                      }}
                    >
                      View all operating areas
                    </Typography>
                  </Paper>
                </Grid>

                {/* Operating Area tiles */}
                {areas.map((area) => (
                  <Grid item xs={12} sm={6} md={4} key={area.short_name || area.name}>
                    <Paper
                      onClick={() => handleAreaClick(area)}
                      sx={{
                        p: 3,
                        cursor: 'pointer',
                        backgroundColor: '#1a1c26',
                        border: '1px solid #333',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: '#f72bef',
                          boxShadow: '0 0 16px rgba(247, 43, 239, 0.2)',
                        },
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 'bold',
                          color: '#00b8ff',
                          mb: 1,
                        }}
                      >
                        {area.short_name || area.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#cacce5',
                        }}
                      >
                        {area.name}
                      </Typography>
                      {latest && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: (latest.byArea[area.short_name || area.name] || 0) > 0 ? '#00e676' : '#8a90a0',
                            display: 'block',
                            mt: 1,
                            fontWeight: 600,
                          }}
                        >
                          {latest.byArea[area.short_name || area.name] || 0} missions · {latest.date}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Grid>

        {/* Right: Help Panel */}
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Help Section */}
            <Paper
              sx={{
                backgroundColor: '#1a1c26',
                border: '1px solid #333',
                p: 3,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  mb: 2,
                  color: '#ffffff',
                }}
              >
                HELP
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#cacce5',
                  }}
                >
                  TRAINING
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#8a90a0',
                  }}
                >
                  Insights on how to get the most value
                </Typography>

                <Box sx={{ my: 1, height: '1px', backgroundColor: '#333' }} />

                <Typography
                  variant="body2"
                  sx={{
                    color: '#cacce5',
                  }}
                >
                  WHAT'S NEW
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#8a90a0',
                  }}
                >
                  Release notes and improvements
                </Typography>

                <Box sx={{ my: 1, height: '1px', backgroundColor: '#333' }} />

                <Link
                  href="/api/docs"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: '#00b8ff',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#00b8ff',
                    }}
                  >
                    API DOCS
                  </Typography>
                </Link>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#8a90a0',
                  }}
                >
                  REST API Documentation
                </Typography>

                <Box sx={{ my: 1, height: '1px', backgroundColor: '#333' }} />

                <Typography
                  variant="body2"
                  sx={{
                    color: '#cacce5',
                  }}
                >
                  SUBMIT A REQUEST
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#8a90a0',
                  }}
                >
                  Report an issue or suggest improvements
                </Typography>
              </Box>
            </Paper>

            {/* Developer Tools Section */}
            <Paper
              sx={{
                backgroundColor: '#1a1c26',
                border: '1px solid #333',
                p: 3,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  mb: 2,
                  color: '#ffffff',
                }}
              >
                DEVELOPER TOOLS
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#8a90a0',
                    }}
                  >
                    Logged In As:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#cacce5',
                    }}
                  >
                    {user ? user.email : 'Anonymous'}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#8a90a0',
                    }}
                  >
                    Roles:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#cacce5',
                    }}
                  >
                    {user && user.roles.length > 0 ? user.roles.join(', ') : '—'}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
